import { IOption } from '../types';
import { Transition, Dialog } from '@headlessui/react';
import { ChevronDownIcon } from 'assets/svg';
import { Fragment, useCallback, useMemo, useState, useRef } from 'react';
import Spinner2 from 'shared/components/Spinner2';
import useOnClickOutside from 'shared/hooks/useOnClickOutside';
import useScreenWidth from 'shared/hooks/useScreenWidth';
import { SCREENS } from 'tailwind/consts';

type Props = {
  placeholder?: string;
  options: Array<IOption>;
  value?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  prefix?: string;
  isLoading?: boolean;
  className?: string;
  mobileBreakpoint?: number;
};

function SingleSelect({
  placeholder,
  options,
  value,
  onChange,
  disabled = false,
  prefix,
  isLoading = false,
  className = '',
  mobileBreakpoint = SCREENS.lg,
}: Props) {
  const screenWidth = useScreenWidth();
  const isMobileScreen = screenWidth < mobileBreakpoint;

  const containerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const selectedOption = useMemo(() => options.find((opt) => opt.value === value), [value, options]);

  const onOptionSelect = useCallback(
    (opt: string) => {
      setIsOpen(false);
      onChange(opt);
    },
    [onChange]
  );

  useOnClickOutside(containerRef, () => isOpen && setIsOpen(false));

  return (
    <div
      {...(!isMobileScreen && { ref: containerRef })}
      className={`tracking-tight relative ${className || (isMobileScreen ? 'w-full' : 'min-w-[172px] max-w-[240px]')}`}
    >
      <button
        disabled={disabled || isLoading}
        onClick={() => setIsOpen((prevState) => !prevState)}
        className={`relative w-full flex rounded-full bg-white px-3.5 ${
          isMobileScreen ? 'py-1.5' : 'py-2'
        } text-left border border-palette-color-bg-grey disabled:text-palette-secondary/40 disabled:border-palette-color-bg-grey/40`}
      >
        {isLoading ? (
          <div className="flex w-full items-center justify-center">
            <Spinner2 className="text-black w-5 h-5" />
          </div>
        ) : (
          <>
            {prefix && (
              <span className="text-sm text-palette-secondary-grey mr-1 whitespace-nowrap">{`${prefix}:`}</span>
            )}
            {selectedOption?.label ? (
              <span className="text-sm text-black truncate pr-8" title={selectedOption.label}>
                {selectedOption.label}
              </span>
            ) : (
              <span className="text-sm text-palette-secondary-grey truncate pr-8">{placeholder || 'Select..'}</span>
            )}
            <div
              className={`pointer-events-none absolute right-6 flex inset-y-0 items-center transition-transform ${
                isOpen ? 'rotate-180' : 'rotate-0'
              }`}
            >
              <ChevronDownIcon className="w-3 text-black" />
            </div>
          </>
        )}
      </button>
      <Transition appear show={isOpen} as={Fragment}>
        {isMobileScreen ? (
          <Dialog as="div" className="relative z-30" onClose={() => setIsOpen(false)}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 backdrop-blur-sm" />
            </Transition.Child>
            <div className="fixed inset-x-0 bottom-0 overflow-y-auto">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-full"
                enterTo="opacity-100 translate-y-0"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-full"
              >
                <Dialog.Panel
                  ref={containerRef}
                  className="max-h-[480px] w-full px-4 py-6 overflow-auto scrollbar flex flex-col rounded-t-xl bg-white"
                >
                  <span className="text-base font-klarheit tracking-tight font-semibold mb-8">
                    {prefix || 'Select value'}
                  </span>
                  {options.map((o) => (
                    <button
                      key={o.value}
                      onClick={() => onOptionSelect(o.value)}
                      className={`py-2 px-2 select-none ${
                        value === o.value ? 'bg-palette-pinkish-gray font-semibold' : 'font-normal'
                      } hover:bg-palette-secondary-light-grey rounded mt-1.5`}
                    >
                      <span title={o.label} className="text-start truncate block text-sm">
                        {o.label}
                      </span>
                    </button>
                  ))}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </Dialog>
        ) : (
          <Transition.Child
            as={Fragment}
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
          >
            <div className="scrollbar absolute mt-2 max-h-[280px] w-full flex flex-col overflow-auto rounded-md bg-white py-2 px-2 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
              {options.map((o) => (
                <button
                  key={o.value}
                  onClick={() => onOptionSelect(o.value)}
                  className={`py-1.5 px-2.5 select-none ${
                    value === o.value ? 'bg-palette-pinkish-gray font-semibold' : 'font-normal'
                  } hover:bg-palette-secondary-light-grey rounded mt-1.5`}
                >
                  <span title={o.label} className="text-start truncate block text-sm">
                    {o.label}
                  </span>
                </button>
              ))}
            </div>
          </Transition.Child>
        )}
      </Transition>
    </div>
  );
}

export default SingleSelect;
